import { classLevelWord, type ClassLevel, type ScheduleEntry } from "@teamna/shared";
import type { ReactNode } from "react";

/**
 * Status pills.
 *
 * Five tones, borrowed from the mobile app's status tokens so a cancelled booking is the same red
 * in both places. Confirmed is deliberately the quietest of them: most of a member's schedule is
 * confirmed, and a screen where every row shouts tells you nothing. `info` (the accent) is for a
 * fact about a class or a person, such as its level or "First class" (C3), not a status.
 */

export type BadgeTone = "confirmed" | "cancelled" | "late" | "quiet" | "info";

/** Each tone names its colour and surface for `.sk-badge` (styles.css), and its text colour. */
const TONES: Record<BadgeTone, string> = {
  confirmed: "[--sk-t:var(--color-confirmed)] [--sk-t-bg:var(--color-confirmed-surface)] text-confirmed",
  cancelled: "[--sk-t:var(--color-cancelled)] [--sk-t-bg:var(--color-cancelled-surface)] text-cancelled",
  late: "[--sk-t:var(--color-late)] [--sk-t-bg:var(--color-late-surface)] text-late",
  quiet: "[--sk-t:var(--color-quiet)] [--sk-t-bg:var(--color-quiet-surface)] text-quiet",
  info: "[--sk-t:var(--color-accent)] [--sk-t-bg:var(--color-accent-surface)] text-accent-strong",
};

export function Badge({ tone = "quiet", children }: { tone?: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={[
        "sk-badge inline-flex items-center gap-1.5 rounded-pill px-2 py-0.5",
        "text-[11px] font-semibold tracking-[0.02em] whitespace-nowrap",
        TONES[tone],
      ].join(" ")}
    >
      <span className="size-1.5 rounded-full bg-current opacity-70" aria-hidden="true" />
      {children}
    </span>
  );
}

/**
 * How a booking's status reads to the member.
 *
 * Mirrors `bookingStatusLabel` in `apps/mobile/src/components/SessionCard.tsx`. A session the
 * business cancelled outranks the booking's own status, because "the business cancelled this" is the
 * only thing the member cares about in that case.
 */
export function bookingStatusLabel(entry: ScheduleEntry): { label: string; tone: BadgeTone } {
  if (entry.session.status === "cancelled") {
    return { label: "Business cancelled", tone: "cancelled" };
  }
  switch (entry.status) {
    case "confirmed":
      return { label: "Confirmed", tone: "confirmed" };
    case "cancelled_by_member":
      return { label: "Cancelled", tone: "quiet" };
    case "cancelled_late":
      return { label: "Late cancel", tone: "late" };
    case "cancelled_by_business":
      return { label: "Business cancelled", tone: "cancelled" };
    case "rescheduled":
      return { label: "Moved", tone: "quiet" };
    default:
      return { label: "Booked", tone: "quiet" };
  }
}

/**
 * The level of a class as a flat badge (Studio S2): "Beginner", "All levels". Nothing when the class
 * has no level, so a family business shows nothing unless the owner set one.
 */
export function ClassLevelBadge({ level }: { level: ClassLevel | null | undefined }) {
  const word = classLevelWord(level);
  return word ? <Badge tone="info">{word}</Badge> : null;
}
